import React from 'react';
import './RangeChart.scss';
import {
  Biomarker,
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO,
  AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerDTO,
  GenderRangeData
} from '../../../../types/apiGeneratedTypes';
import {
  getAgeFromDOB,
  getClosestAgeRangeEntry,
  isAgeDependentRange,
  parseNumericValue
} from '../../../../utils/biomarkers';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';

interface RangeChartProps {
  biomarker:
    | Biomarker
    | AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO
    | AppScenariosBiomarkerGetResultsByAppointmentApplicationPayloadBiomarkerDTO;
  value: number | string;
  scale: GenderRangeData | null;
  maxWidth?: string;
  width?: string;
  minWidth?: string;
}

const RangeChart: React.FC<RangeChartProps> = ({
  biomarker,
  value,
  scale,
  maxWidth,
  width,
  minWidth
}) => {
  const { memberData } = useSelector((state: RootState) => state.member);

  // Check if this is a "Units + Values" biomarker
  const isUnitsBiomarker = biomarker.biomarker_type === 'UNITS';
  
  // For "Units + Values" biomarkers, don't show the range chart as charts are not meaningful
  if (isUnitsBiomarker) {
    return null;
  }

  const parsedValue =
    biomarker.biomarker_type === 'BINARY' || biomarker.name.toLowerCase().includes('anti-nuclear') 
      ? value 
      : parseNumericValue(value);

  if ((biomarker.biomarker_type === 'BINARY' || biomarker.name.toLowerCase().includes('anti-nuclear')) && 'value_meaning' in biomarker) {
    return (
      <div className='range-chart binary' style={{ maxWidth, width, minWidth }}>
        <div className='positive-segment' />
        <div className='negative-segment' />
        <div
          className='indicator'
          style={{
            left: `${
              biomarker.value_meaning === 'POSITIVE_NORMAL'
                ? parsedValue === 'POSITIVE'
                  ? '75'
                  : '25'
                : parsedValue === 'POSITIVE'
                  ? '25'
                  : '75'
            }%`
          }}
        />
      </div>
    );
  }

  if (!scale || !parsedValue) return null;

  let rangeData = null;

  if (isAgeDependentRange(scale)) {
    const age = getAgeFromDOB(memberData?.dob!);
    rangeData = getClosestAgeRangeEntry(scale, age);
  } else {
    rangeData = scale;
  }

  const segments = [
    {
      label: 'critical-low',
      from: rangeData?.low_from,
      to: rangeData?.low_to
    },
    { label: 'low', from: rangeData?.below_optimal_from, to: rangeData?.below_optimal_to },
    {
      label: 'optimal',
      from: rangeData?.optimal_from,
      to: rangeData?.optimal_to
    },
    { label: 'high', from: rangeData?.above_optimal_from, to: rangeData?.above_optimal_to },
    {
      label: 'critical-high',
      from: rangeData?.high_from,
      to: rangeData?.high_to
    }
  ];

  // Compute indicator position as before.
  // const getIndicatorPosition = () => {
  //   const parsed = Number(parsedValue);
  //   if (isNaN(parsed)) return 0;

  //   if (parsed <= segments[0].from) return 0;
  //   if (parsed >= segments[4].to) return 100;

  //   for (let i = 0; i < segments.length; i++) {
  //     const { from, to } = segments[i];
  //     if (parsed >= from && parsed <= to) {
  //       const fraction = (parsed - from) / (to - from);
  //       return i * 20 + fraction * 20; // i segments at 20% each
  //     }
  //   }
  //   return 0;
  // };

  const getIndicatorPosition = () => {
    const parsed = Number(parsedValue);
    if (isNaN(parsed)) return 0;

    const validSegments = segments
      .map((segment, index) => ({ ...segment, index }))
      .filter(
        (s): s is { label: string; from: number; to: number; index: number } =>
          typeof s.from === 'number' &&
          typeof s.to === 'number' &&
          !isNaN(s.from) &&
          !isNaN(s.to)
      );

    if (!validSegments.length) return 0;

    const totalSegments = segments.length;
    const segmentWidth = 100 / totalSegments;

    for (const s of validSegments) {
      const { from, to, index } = s;
      if (parsed >= from && parsed <= to) {
        const fraction = (parsed - from) / (to - from);
        return index * segmentWidth + fraction * segmentWidth;
      }
    }

    const firstValid = validSegments[0];
    if (parsed < firstValid.from) {
      return firstValid.index * segmentWidth;
    }

    const lastValid = validSegments[validSegments.length - 1];
    if (parsed > lastValid.to) {
      return (lastValid.index + 1) * segmentWidth;
    }

    return 0;
  };

  return (
    <div className='range-chart' style={{ maxWidth, width, minWidth }}>
      {segments.map((segment, index) => (
        <div key={index} className={`segment ${segment.label}`} />
      ))}
      <div
        className='indicator'
        style={{ left: `${getIndicatorPosition()}%` }}
      />
    </div>
  );
};

export default RangeChart;
